import type { SavedDeckSelectionRef } from '../../server/protocol.js';
import type { GameSeat } from '../client/url.js';

export interface OnboardingStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const CLAIM_OPERATION_KEY = 'cards:onboarding:claim-operation:v1';
const AUTO_START_PENDING_KEY = 'cards:onboarding:auto-start-pending:v1';
const SOLO_SEAT_PREFIX = 'cards:onboarding:solo-seat:v1:';
const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u;

function soloSeatKey(seat: GameSeat): string {
  return `${SOLO_SEAT_PREFIX}${encodeURIComponent(seat.room)}:${encodeURIComponent(seat.playerId)}`;
}

export function claimOperationId(
  storage: OnboardingStorage,
  generate: () => string = () => crypto.randomUUID(),
): string {
  const existing = storage.getItem(CLAIM_OPERATION_KEY);
  if (existing !== null && UUID_V4_PATTERN.test(existing)) return existing;
  const operationId = generate();
  if (!UUID_V4_PATTERN.test(operationId)) throw new Error('Не удалось создать идентификатор запуска обучения');
  storage.setItem(CLAIM_OPERATION_KEY, operationId);
  if (storage.getItem(CLAIM_OPERATION_KEY) !== operationId) {
    throw new Error('Браузер не может сохранить запуск обучения');
  }
  return operationId;
}

export function clearClaimOperation(storage: OnboardingStorage): void {
  storage.removeItem(CLAIM_OPERATION_KEY);
}

export function markAutoStartPending(storage: OnboardingStorage): void {
  storage.setItem(AUTO_START_PENDING_KEY, '1');
  if (storage.getItem(AUTO_START_PENDING_KEY) !== '1') {
    throw new Error('Браузер не может сохранить переход к матчу');
  }
}

export function autoStartPending(storage: OnboardingStorage): boolean {
  return storage.getItem(AUTO_START_PENDING_KEY) === '1';
}

export function clearAutoStartPending(storage: OnboardingStorage): void {
  storage.removeItem(AUTO_START_PENDING_KEY);
}

export function markOnboardingSoloSeat(
  storage: OnboardingStorage,
  seat: GameSeat,
  deck: SavedDeckSelectionRef,
): void {
  const value = JSON.stringify({ deckId: deck.deckId, revision: deck.revision });
  storage.setItem(soloSeatKey(seat), value);
  if (storage.getItem(soloSeatKey(seat)) !== value) {
    throw new Error('Браузер не может сохранить onboarding solo');
  }
}

export function isOnboardingSoloSeat(storage: OnboardingStorage, seat: GameSeat): boolean {
  const value = storage.getItem(soloSeatKey(seat));
  if (value === null) return false;
  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;
    return typeof parsed.deckId === 'string'
      && Number.isSafeInteger(parsed.revision)
      && (parsed.revision as number) > 0;
  } catch {
    return false;
  }
}

export function promoteOnboardingSoloSeat(
  storage: OnboardingStorage,
  from: GameSeat,
  to: GameSeat,
): void {
  const value = storage.getItem(soloSeatKey(from));
  if (value === null) return;
  storage.setItem(soloSeatKey(to), value);
  if (storage.getItem(soloSeatKey(to)) !== value) {
    throw new Error('Браузер не может перенести onboarding solo в комнату');
  }
  storage.removeItem(soloSeatKey(from));
}

export function clearOnboardingSoloSeats(
  storage: OnboardingStorage,
  ...seats: Array<GameSeat | undefined>
): void {
  for (const seat of seats) {
    if (seat !== undefined) storage.removeItem(soloSeatKey(seat));
  }
}
